import { client } from "@/hera-api/client/client.gen";

export interface CompanyAnswer {
  name: string;
  company_name: string;
  email: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
}

export interface CompanyQuestionTab {
  key: string;
  label: string;
  question_ids: string[];
}

export interface CompanyAnswerRow {
  id: string;
  primary_account_id: string | null;
  registrant: string;
  company_name: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  license_count: number;
  answer: CompanyAnswer;
}

export interface CompanyAnswerFilters {
  company_name?: string[];
  name?: string[];
  email?: string[];
  phone?: string[];
  address?: string[];
  registrant?: string[];
}

const SECURITY = [{ scheme: "bearer", type: "http" }] as const;

async function readError(error: unknown, fallback: string): Promise<never> {
  const body = error as {
    detail?: unknown;
    error_content?: { detail?: unknown };
  } | null;
  const detail = body?.error_content?.detail ?? body?.detail;
  if (typeof detail === "string" && detail.trim()) {
    throw new Error(detail);
  }
  throw new Error(fallback);
}

export async function listCompanyQuestionTabs(): Promise<CompanyQuestionTab[]> {
  const result = await client.get({
    url: "/operator/company-question-answers",
    security: SECURITY,
  });
  if (result.error)
    await readError(result.error, "Could not load company questions");
  return (result.data as { tabs: CompanyQuestionTab[] }).tabs;
}

export async function listCompanyQuestionAnswers(
  tabKey: string,
): Promise<CompanyAnswerRow[]> {
  const result = await client.get({
    url: `/operator/company-question-answers/${encodeURIComponent(tabKey)}`,
    security: SECURITY,
  });
  if (result.error) await readError(result.error, "Could not load answers");
  return (result.data as { rows: CompanyAnswerRow[] }).rows;
}

export async function rewriteCompanyQuestionAnswers(
  tabKey: string,
  body: {
    source: CompanyAnswer;
    replacement: CompanyAnswer;
    filters: CompanyAnswerFilters;
  },
): Promise<number> {
  const result = await client.post({
    url: `/operator/company-question-answers/${encodeURIComponent(tabKey)}/rewrite`,
    body,
    security: SECURITY,
  });
  if (result.error) await readError(result.error, "Could not update answers");
  return (result.data as { updated_license_count: number })
    .updated_license_count;
}
